// @ts-strict-ignore
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Link, RichTextEditor, Spacer, Typography } from "@aviary";
import { AuthFooter } from "@shared/components/AuthFooter/AuthFooter";
import { AuthHeader } from "@shared/components/AuthHeader/AuthHeader";
import { AuthHeaderWrapper } from "@shared/components/AuthHeaderWrapper/AuthHeaderWrapper";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { Page } from "@unauthenticated/signupSignin/components/Page/Page";
import { PageContent } from "@unauthenticated/signupSignin/components/PageContent/PageContent";
import { SignUpHeader } from "@unauthenticated/signupSignin/components/SignUpHeader/SignUpHeader";
import { StoreLogo } from "@unauthenticated/signupSignin/components/StoreLogo/StoreLogo";
import { l } from "@unauthenticated/signupSignin/locales/i18n";
import { OfficeResendInvitationForm } from "@unauthenticated/signupSignin/scenes/OfficeResendInvitationPage/OfficeResendInvitationForm/OfficeResendInvitationForm";

import * as styles from "./OfficeResendInvitationPage.styles";

type StoreAttributes = {
  storeId: string;
  storeLogo?: string;
  storeName: string;
  activationSuccessMessage: string | null;
};

interface Props {
  storeAttributes: StoreAttributes;
}

const OfficeResendInvitationPage = ({ storeAttributes }: Props) => {
  const { storeId, storeName, storeLogo, activationSuccessMessage } = storeAttributes;
  const [email, setEmail] = useState(null);
  const { tablet } = useBreakpoints();
  const { t } = useTranslation();

  const renderForm = () => (
    <>
      <Typography>{t(l.officeSignUp.ResendInvitation)}</Typography>

      <OfficeResendInvitationForm storeId={storeId} onSuccess={emailUsed => setEmail(emailUsed)} />
      <Typography isMarginless type={tablet.greaterThan ? "body" : "footnote"}>
        {t(l.common.HaveAcount)}
        &nbsp;
        <Link href={authRoutes.login}>{t(l.common.SignIn)}</Link>
      </Typography>
    </>
  );

  const renderSuccess = () => (
    <>
      <Typography>{t(l.officeSignUp.ResendInvitationSuccess, { email })}</Typography>
      <Spacer height="one" />
      {!!activationSuccessMessage && (
        <RichTextEditor initialInput={activationSuccessMessage} readOnly />
      )}
    </>
  );

  return (
    <Page>
      <PageContent>
        <AuthHeaderWrapper>
          <AuthHeader route={authRoutes.login} />
        </AuthHeaderWrapper>
        <div css={styles.contentLayout}>
          <SignUpHeader>
            <StoreLogo storeLogo={storeLogo} storeName={storeName} />
            <Typography type={tablet.greaterThan ? "h2" : "h3"}>{storeName}</Typography>
          </SignUpHeader>
          {email ? renderSuccess() : renderForm()}
        </div>
      </PageContent>
      <AuthFooter />
    </Page>
  );
};

export { OfficeResendInvitationPage };
